import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

const PolicySection = ({ title, children }) => (
  <section>
    <h2 className="text-2xl font-semibold text-gray-800 border-l-4 border-yellow-400 pl-3 mb-3">
      {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const PrivacyPolicyRider = () => {
  return (
    <section className="min-h-screen w-screen bg-gradient-to-b from-[#fefef8] to-[#fafae0] flex flex-col items-center Poppins">
      <Navbar />

      <div className="pt-[60px] w-full flex justify-center px-6">
        <div className="max-w-7xl w-full bg-white shadow-lg rounded-2xl border border-yellow-300 p-6 lg:p-10">
          <h1 className="text-3xl lg:text-4xl font-extrabold text-gray-800 text-center mb-6 tracking-wide">
            Privacy Policy for Riders
          </h1>
          <p className="text-center text-gray-500 mb-8">
            Last updated: <time dateTime="2026-10-28" className="font-medium">28 October 2026</time>
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <p>
              This policy explains how Instaport Delivery (“Instaport”, “we”, “us”)
              collects, uses and shares information through the Instaport Rider
              app and related delivery-partner services. It covers registration,
              verification, nearby order alerts, deliveries, live tracking,
              earnings, payments and support. Permissions and features may differ
              between Android and iOS and depend on your device settings.
            </p>

            <PolicySection title="1. Information We Collect">
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong>Account and profile:</strong> name, mobile number,
                  login credentials, rider ID, profile photo, age, address,
                  service city/area, vehicle details and referral information.
                </li>
                <li>
                  <strong>Verification:</strong> identity and vehicle information
                  you submit, including Aadhaar, PAN, driving licence and vehicle
                  registration documents, document photos, verification results,
                  and reference contacts you provide. Please obtain your
                  references’ permission before sharing their details.
                </li>
                <li>
                  <strong>Delivery activity:</strong> availability status, order
                  offers and responses, assigned orders, pickup/drop locations,
                  timestamps, status updates, delivery photos, cancellations,
                  earnings, incentives, points and penalties.
                </li>
                <li>
                  <strong>Financial information:</strong> account-holder name,
                  bank account number, IFSC, UPI ID, bank-verification results,
                  wallet balances, payment and payout references, amounts,
                  transaction status and receipts.
                </li>
                <li>
                  <strong>Support and technical information:</strong> messages
                  and attachments you send, device model/name, operating system,
                  app version, permission status, notification tokens, network
                  and diagnostic information, and app interaction events.
                </li>
              </ul>
            </PolicySection>

            <PolicySection title="2. Location and Background Tracking">
              <p>
                Instaport Rider collects and sends precise GPS and, where
                available, approximate location to Instaport to find nearby
                orders, validate pickup distance, support navigation, verify
                pickup/drop activity and provide live delivery tracking.
                Updates may include accuracy and the time of the location fix.
                The app also checks for mock-location signals to help prevent
                false pickup or delivery locations.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong>Android:</strong> after the location disclosure and
                  permission flow, location can be collected while you are
                  online/available for nearby order alerts and during active
                  deliveries, including when you use another app, minimize
                  Instaport Rider or lock the screen. An ongoing foreground
                  service notification indicates background tracking.
                </li>
                <li>
                  <strong>iOS:</strong> background location is used during active
                  deliveries, with the required location authorization, so
                  tracking can continue while the app is minimized or the
                  screen is locked.
                </li>
                <li>
                  <strong>Controls:</strong> going offline stops availability
                  tracking; tracking for an active delivery may continue until
                  that delivery ends. You can change location permission or
                  turn off device location in system settings. This can prevent
                  nearby alerts, order acceptance, pickup/drop verification and
                  live tracking. Device restrictions, force-stopping the app or
                  loss of connectivity can interrupt updates.
                </li>
              </ul>
              <p>
                Relevant rider location is shared with the customer associated
                with an assigned delivery for tracking and with authorized
                operations/support staff. Mapping providers receive coordinates
                or address queries needed for map and route services. We do not
                sell rider location data to advertisers or unrelated third parties.
              </p>
            </PolicySection>

            <PolicySection title="3. Display Over Other Apps — Android Overlay Permission">
              <p>
                Instaport Rider requests Android’s “Display over other apps”
                permission to show floating nearby-order cards and the Instaport
                Rider shortcut while you are using another app. Order cards can
                display pickup/drop addresses, estimated earnings, payment type,
                distance and an acceptance timer. Swiping to accept sends your
                response and the location needed to validate assignment to Instaport.
              </p>
              <p>
                The shortcut helps online riders return to Instaport Rider.
                This permission is used to display our own controls; we do not
                use it to read or record other apps’ screen contents, passwords
                or messages. Location access is controlled separately.
              </p>
              <p>
                You grant or revoke this access in Android Settings → Apps →
                Special app access → Display over other apps → Instaport Rider
                (names may vary by device). Disabling it prevents floating
                order cards and the shortcut from appearing. Orders within the
                app still depend on your account, location and delivery eligibility.
              </p>
            </PolicySection>

            <PolicySection title="4. Notifications, Camera, Photos and Files">
              <ul className="list-disc list-inside space-y-2">
                <li>
                  <strong>Notifications:</strong> push tokens deliver order
                  offers, assignment/cancellation updates, wallet and support
                  alerts. Alerts may include sounds, floating or full-screen
                  order prompts where permitted. Background services also show
                  status notifications. Order information may appear on the lock
                  screen depending on your settings; you can control notification
                  access and previews there.
                </li>
                <li>
                  <strong>Camera and selected media:</strong> used when you take
                  or select profile pictures, verification documents, delivery
                  proof or support attachments. Content you submit is uploaded
                  to provide the relevant service. Photo/file access may also
                  be used to save or share receipts you request.
                </li>
                <li>
                  <strong>Network and background operation:</strong> internet
                  access connects the app to delivery, payment and support
                  services. Android foreground services and wake locks support
                  tracking and time-sensitive alerts. Battery or background
                  restrictions can affect their delivery.
                </li>
              </ul>
              <p>
                You can review camera, media and notification permissions in
                device settings. Declining or revoking a permission can limit
                the feature that needs it.
              </p>
            </PolicySection>

            <PolicySection title="5. How We Use and Share Information">
              <p>
                We use this information to manage rider accounts and identity
                checks, allocate and track deliveries, calculate earnings,
                process payments and settlements, handle disputes and support,
                prevent fraud, troubleshoot failures and improve the service.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>
                  Customers involved in a delivery can receive rider identity/contact
                  information, order progress, relevant delivery proof and location
                  needed to coordinate and track it.
                </li>
                <li>
                  Authorized Instaport teams use account, verification, location,
                  order, payment and support records to operate the service and
                  resolve issues.
                </li>
                <li>
                  Service providers process relevant information for hosting,
                  media storage, maps, notifications, analytics, verification
                  and payments. These include Google Maps/Firebase,
                  Razorpay or Cashfree for the selected payment flow,
                  Digiverification for applicable PAN/bank checks, and media
                  hosting providers such as Cloudinary.
                </li>
                <li>
                  We may disclose relevant records when required by applicable
                  law or lawful requests, or to investigate fraud, disputes or
                  threats to users and the service.
                </li>
              </ul>
              <p>
                Payment details entered into a payment provider’s checkout are
                processed by that provider. Instaport uses transaction
                references, amounts and status to update wallets and receipts;
                bank/UPI details you provide are used for verification and
                payouts. Never send your UPI PIN, payment OTP or card security
                code to support or in chat.
              </p>
            </PolicySection>

            <PolicySection title="6. Analytics and Device Information">
              <p>
                We use Google Analytics for Firebase to understand app use and
                measure marketing performance. Events include app opens, login,
                registration and vehicle type, order acceptance, and successful
                wallet-payment amount/currency. Firebase services may process
                app-instance/device identifiers, usage and diagnostic data.
                On Android, Analytics may collect the Advertising ID when
                available and permitted by device settings. Your device’s
                advertising privacy controls let you manage that identifier;
                changing it does not disable all app analytics.
              </p>
              <p>
                We also send app version, device/OS information and the status
                of location, notification, camera, media and overlay permissions
                to Instaport to help diagnose device-specific problems. Read{" "}
                <a className="text-blue-600 hover:underline" href="https://firebase.google.com/support/privacy">
                  Firebase’s privacy information
                </a>{" "}
                and the{" "}
                <a className="text-blue-600 hover:underline" href="https://policies.google.com/privacy">
                  Google Privacy Policy
                </a>{" "}
                for information about these providers’ data practices.
              </p>
            </PolicySection>

            <PolicySection title="7. Storage, Security and Retention">
              <p>
                Account and service records are stored on Instaport’s backend
                and service providers used for the relevant feature.
                Session credentials, preferences and some cached data are also
                stored on your device. Authentication and access controls protect
                account and administrative functions. No storage or transmission
                method can guarantee absolute security.
              </p>
              <p>
                Account, verification, delivery, location, transaction and
                support records are retained to operate your account, reconcile
                payments, address disputes and fraud, and meet applicable legal
                or accounting obligations. Retention depends on the type of
                record and the reason it is needed. Turning off a permission
                stops the access it controls; it does not erase previously
                submitted records. Contact us to request information about
                retention or deletion of your records.
              </p>
            </PolicySection>

            <PolicySection title="8. Your Choices and Account Deletion">
              <p>
                You can update available profile details in the app, manage
                permissions in device settings, and contact support to request
                access to, correction of or deletion of your personal information.
                We may need to verify that a request relates to your account.
              </p>
              <p>
                To start account deletion, use <strong>Profile → Delete Account</strong>{" "}
                in Instaport Rider. You can also use our{" "}
                <Link to="/deleteaccount" className="text-red-600 font-medium hover:underline">
                  account deletion instructions
                </Link>{" "}
                or email{" "}
                <a href="mailto:support@instaportdelivery.com" className="text-blue-600 hover:underline break-words">
                  support@instaportdelivery.com
                </a>{" "}
                with your registered mobile number and rider ID.
              </p>
              <p>
                The current in-app delete-account action signs you out and
                removes the current session, push token and
                latest location from the rider account. It does not immediately
                erase every backend record: profile/verification and related
                delivery, payment or support records may remain. For deletion
                of retained personal records, contact support; records required
                for legal obligations, payment reconciliation, fraud prevention
                or unresolved disputes may need to be retained. Uninstalling
                the app alone does not delete your account or backend records.
              </p>
            </PolicySection>

            <PolicySection title="9. Policy Updates">
              <p>
                We may update this policy as app features, service providers or
                data practices change. The date above identifies the latest
                version. Where required, we will provide additional notice or
                request consent through the app or another appropriate channel.
              </p>
            </PolicySection>

            <PolicySection title="10. Contact Us">
              <p>
                For privacy questions, permission concerns or requests about
                your personal information, contact Instaport Delivery:
              </p>
              <div className="mt-2 bg-gray-50 p-4 rounded-lg shadow-sm text-sm space-y-2">
                <p>
                  <span className="font-medium">Address:</span> Chembur, MG
                  R/3 A Wing ground floor Mass Metropolis Chembur Mumbai Maharashtra 400071
                </p>
                <p>
                  <span className="font-medium">Email:</span>{" "}
                  <a href="mailto:support@instaportdelivery.com" className="text-blue-600 hover:underline break-words">
                    support@instaportdelivery.com
                  </a>
                </p>
                <p>
                  <span className="font-medium">Working Hours:</span> Mon–Sat:
                  10am – 8pm, Sun: 10am – 6pm
                </p>
              </div>
            </PolicySection>
          </div>
        </div>
      </div>

      <Footer />
    </section>
  );
};

export default PrivacyPolicyRider;
